import { type Ref, readonly, ref } from "vue";

/**
 * Tracks whether the user has taken manual control of the diagram viewport
 * (zoom or pan) for the currently loaded diagram.
 *
 * bpmn-js fires `canvas.viewbox.changed` for *every* viewbox change, both
 * programmatic (fit-to-viewport, zoom-to-element, viewbox restore, import) and
 * genuine user gestures (wheel zoom, scroll/pan, drag-pan, pinch, zoom buttons).
 * We wrap our own programmatic changes with the suppression counter so they are
 * ignored; anything left over is treated as a manual interaction.
 *
 * Consumers (Process/Definition.vue) read `userHasAdjustedView` to suppress the
 * ResizeObserver-driven auto-fit once the user has taken control, so a transient
 * container resize — e.g. a hover popup briefly toggling a scrollbar — no longer
 * snaps the diagram back to fit and discards the user's zoom.
 */
export interface ViewTracker {
    /** True once the user has manually zoomed/panned the current diagram. */
    readonly userHasAdjustedView: Readonly<Ref<boolean>>;
    /** Run a synchronous programmatic viewport change without tracking it. */
    runProgrammatic: <T>(fn: () => T) => T;
    /**
     * Run a programmatic viewport change whose event does not arrive until a
     * later animation frame, holding the suppression open until it does.
     *
     * panzoom (EntitiesDiagram's canvas) works this way: `moveTo`/`zoomAbs`
     * update its transform synchronously but only apply it — and fire
     * `transform` — from a `requestAnimationFrame` callback. The synchronous
     * window above has already closed by then, so our own camera moves would
     * be indistinguishable from a wheel or a drag.
     */
    runDeferredProgrammatic: <T>(fn: () => T) => T;
    /** Begin an async programmatic change (pair with `endProgrammatic`). */
    beginProgrammatic: () => void;
    /** End an async programmatic change started with `beginProgrammatic`. */
    endProgrammatic: () => void;
    /** Handle a `canvas.viewbox.changed` event from bpmn-js. */
    handleViewboxChanged: () => void;
    /** Reset to the automatic state (e.g. when a new diagram is loaded). */
    reset: () => void;
}

export function createViewTracker(): ViewTracker {
    const userHasAdjustedView = ref(false);
    let suppressed = 0;

    const beginProgrammatic = () => {
        suppressed += 1;
    };

    const endProgrammatic = () => {
        suppressed = Math.max(0, suppressed - 1);
    };

    const runProgrammatic = <T>(fn: () => T): T => {
        beginProgrammatic();
        try {
            return fn();
        } finally {
            endProgrammatic();
        }
    };

    const runDeferredProgrammatic = <T>(fn: () => T): T => {
        beginProgrammatic();
        try {
            return fn();
        } finally {
            // Not `endProgrammatic()` directly: the event this is suppressing
            // has not been fired yet. Overlapping calls are safe — the counter
            // only reaches zero once every one of them has released.
            requestAnimationFrame(() => endProgrammatic());
        }
    };

    const handleViewboxChanged = () => {
        if (suppressed === 0) {
            userHasAdjustedView.value = true;
        }
    };

    const reset = () => {
        userHasAdjustedView.value = false;
    };

    return {
        userHasAdjustedView: readonly(userHasAdjustedView),
        runProgrammatic,
        runDeferredProgrammatic,
        beginProgrammatic,
        endProgrammatic,
        handleViewboxChanged,
        reset,
    };
}
